import {
  Modal as AriaModal,
  Dialog,
  type DialogProps,
  ModalOverlay,
  type ModalOverlayProps,
} from "react-aria-components"
import { twMerge } from "tailwind-merge"
import { Body, ButtonGroup, Footer } from "./common"
import { Header } from "./Header"

export type ModalProps = Omit<ModalOverlayProps, "children" | "className"> & {
  "aria-label"?: string
  animationEnterDuration?: number
  animationExitDuration?: number
  className?: string
  overlayClassName?: string
  children: DialogProps["children"]
  hasDividers?: boolean
  modalProps?: Omit<ModalProps, "children">
  size?:
    | "auto"
    | "xsmall"
    | "small"
    | "medium"
    | "large"
    | "xlarge"
    | "almost-full"
    | "full"
}

/**
 * Popover content that prevents interaction with the rest of the page. Useful
 * for confirming actions or moving complexity out of the main view. Requires
 * passing `isOpen` state and `onOpenChange` handler to manage visibility. If
 * you don't need to control the open state, use ModalWithTrigger.
 *
 * ```tsx
 * import { Button } from "@tokenterminal/core/ui/button/Button"
 * import { Modal } from "@tokenterminal/core/ui/overlays/modal/Modal"
 *
 * <Button onClick={() => setIsOpen(true)}>Open modal</Button>
 * <Modal isOpen={isOpen} onOpenChange={setIsOpen}>
 *   Modal content
 * </Modal>
 * ```
 */

export const Modal = ({
  "aria-label": ariaLabel,
  animationEnterDuration = 400,
  animationExitDuration = 200,
  children,
  isDismissable = true,
  size = "medium",
  hasDividers,
  className,
  isKeyboardDismissDisabled = false,
  overlayClassName,
  modalProps,
  style,
  ...props
}: ModalProps) => {
  return (
    <ModalOverlay
      isOpen={props.isOpen}
      isDismissable={isDismissable}
      className={twMerge("tt-modal-overlay", overlayClassName)}
      isKeyboardDismissDisabled={isKeyboardDismissDisabled}
      style={
        {
          "--animation-enter-duration": `${animationEnterDuration}ms`,
          "--animation-exit-duration": `${animationExitDuration}ms`,
          "--animation-timing-function": "cubic-bezier(.21,.98,0,1)",
          ...style,
        } as unknown as React.CSSProperties
      }
      {...props}
    >
      <div className="tt-modal-overlay-scrim" />
      <AriaModal
        isDismissable={isDismissable}
        className={twMerge("tt-modal", className)}
        data-has-dividers={hasDividers}
        aria-label={ariaLabel}
        {...modalProps}
      >
        <Dialog
          aria-label={ariaLabel}
          className="tt-modal-dialog"
          data-size={size}
        >
          {children}
        </Dialog>
      </AriaModal>
    </ModalOverlay>
  )
}

Modal.Header = Header
Modal.Body = Body
Modal.ButtonGroup = ButtonGroup
Modal.Footer = Footer
